import { format, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';

export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

/**
 * Lista de timezones mais comuns do Brasil e do mundo
 */
export const COMMON_TIMEZONES = [
  // Brasil
  { value: 'America/Sao_Paulo', label: 'Brasília (UTC-3)', region: 'Brasil' },
  { value: 'America/Manaus', label: 'Manaus (UTC-4)', region: 'Brasil' },
  { value: 'America/Rio_Branco', label: 'Rio Branco (UTC-5)', region: 'Brasil' },
  { value: 'America/Noronha', label: 'Fernando de Noronha (UTC-2)', region: 'Brasil' },
  
  // Américas
  { value: 'America/New_York', label: 'Nova York (UTC-5/-4)', region: 'América do Norte' },
  { value: 'America/Chicago', label: 'Chicago (UTC-6/-5)', region: 'América do Norte' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/-7)', region: 'América do Norte' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (UTC-3)', region: 'América do Sul' },
  { value: 'America/Santiago', label: 'Santiago (UTC-4/-3)', region: 'América do Sul' },
  
  // Europa
  { value: 'Europe/London', label: 'Londres (UTC+0/+1)', region: 'Europa' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1/+2)', region: 'Europa' },
  { value: 'Europe/Berlin', label: 'Berlim (UTC+1/+2)', region: 'Europa' },
  { value: 'Europe/Madrid', label: 'Madrid (UTC+1/+2)', region: 'Europa' },
  { value: 'Europe/Lisbon', label: 'Lisboa (UTC+0/+1)', region: 'Europa' },
  
  // Ásia
  { value: 'Asia/Tokyo', label: 'Tóquio (UTC+9)', region: 'Ásia' },
  { value: 'Asia/Shanghai', label: 'Xangai (UTC+8)', region: 'Ásia' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+4)', region: 'Ásia' },
  
  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10/+11)', region: 'Oceania' },
  
  // UTC
  { value: 'UTC', label: 'UTC (UTC+0)', region: 'UTC' },
];

/**
 * Agrupa timezones por região
 */
export const TIMEZONES_BY_REGION = COMMON_TIMEZONES.reduce((acc, tz) => {
  if (!acc[tz.region]) {
    acc[tz.region] = [];
  }
  acc[tz.region].push(tz);
  return acc;
}, {} as Record<string, typeof COMMON_TIMEZONES>);

/**
 * Converte uma data UTC para o timezone do usuário
 */
export function toUserTimezone(date: Date | string, timezone: string = DEFAULT_TIMEZONE): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return toZonedTime(dateObj, timezone);
}

/**
 * Converte uma data do timezone do usuário para UTC
 */
export function fromUserTimezone(date: Date, timezone: string = DEFAULT_TIMEZONE): Date {
  return fromZonedTime(date, timezone);
}

/**
 * Formata uma data no timezone do usuário
 */
export function formatInUserTimezone(
  date: Date | string,
  formatStr: string,
  timezone: string = DEFAULT_TIMEZONE
): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, timezone, formatStr, { locale: ptBR });
}

/**
 * Retorna a data de hoje no timezone do usuário (formato ISO: YYYY-MM-DD)
 */
export function getTodayInTimezone(timezone: string = DEFAULT_TIMEZONE): string {
  return formatInUserTimezone(new Date(), 'yyyy-MM-dd', timezone);
}

/**
 * Retorna o horário atual no timezone do usuário
 */
export function getNowInTimezone(timezone: string = DEFAULT_TIMEZONE): Date {
  return toUserTimezone(new Date(), timezone);
}

/**
 * Verifica se uma data está no passado considerando o timezone
 */
export function isPastDate(date: Date | string, timezone: string = DEFAULT_TIMEZONE): boolean {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const now = getNowInTimezone(timezone);
  return dateObj < now;
}

/**
 * Verifica se uma data é hoje considerando o timezone
 */
export function isToday(date: Date | string, timezone: string = DEFAULT_TIMEZONE): boolean {
  const dateStr = typeof date === 'string' ? date : formatInUserTimezone(date, 'yyyy-MM-dd', timezone);
  const todayStr = getTodayInTimezone(timezone);
  return dateStr === todayStr;
}

/**
 * Retorna data formatada de forma legível
 */
export function formatDateFriendly(date: Date | string, timezone: string = DEFAULT_TIMEZONE): string {
  return formatInUserTimezone(date, "d 'de' MMMM 'de' yyyy", timezone);
}

/**
 * Retorna data e hora formatadas
 */
export function formatDateTimeFriendly(date: Date | string, timezone: string = DEFAULT_TIMEZONE): string {
  return formatInUserTimezone(date, "d 'de' MMMM 'de' yyyy 'às' HH:mm", timezone);
}
